import React, { useEffect } from "react";
import _ from 'lodash';
import cx from 'classnames';
import { animated, useTransition } from '@react-spring/web';
import styles from './thumbnail.module.scss';
import { Banner } from "src/types/common";
import Thumbnail from "src/components/thumbnail";
import Slider from "src/components/slider";
import DefaultBackground from 'src/legacy/images/collections/DefaultBackground.jpeg';
import Tooltip from "src/components/tooltip";
import Button from "src/components/button";
import { MediaLibraryChooser } from "src/components/mediaLibrary";
import { Cropper, ReactCropperElement } from "react-cropper";
import useMeasure from "react-use-measure";
import { nanoid } from "nanoid";
import { MediaFile } from "src/types/media";
import { MediaLibrary } from "src/types/mediaLibrary";
import { toast } from "react-toastify";
import { parseMediaMetadata } from "src/utils/media.service";
import { useDispatch } from "react-redux";
import { invalidateMediaUpload, uploadMedia } from "src/redux/actions/media";
import { useSelector } from "react-redux";
import Actions from "src/components/actionbar";
import { POSITIONS } from "src/constants/banner";

interface EditThumbnailProps {
  show: boolean;
  banner: Banner | null;
  type: 'course' | 'simulation' | 'podcast';
  entityType?: 'discovery' | 'coldCall' | 'elevatorPitch'
  defaultBanner: string;

  // eslint-disable-next-line no-unused-vars
  onFinish: (banner: Banner) => void;
  onClose: () => void;
}

const EditCourseThumbnail: React.FC<EditThumbnailProps> = ({ show, type, entityType, banner, defaultBanner, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const [editAction, setEditAction] = React.useState<'edit' | 'crop' | 'position' | null>(null);
  const [bannerState, setBannerState] = React.useState<Banner | null>(banner);
  const [originalBanner, setOriginalBanner] = React.useState<Banner | null>(banner);

  const uploading = useSelector((state: any) => {
    const background = _.get(bannerState, `background`);
    const entityType = _.get(background, 'entityType');
    if (background && entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loading`, false);
    }

    return false;
  });

  const uploaded = useSelector((state: any) => {
    const background = _.get(bannerState, `background`);
    const entityType = _.get(background, 'entityType');
    if (background && entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loaded`, false);
    }

    return false;
  });

  const uploadedBackground = useSelector((state: any) => {
    const background = _.get(bannerState, `background`);
    const entityType = _.get(background, 'entityType');
    if (background && entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].item`);
    }

    return false;
  });

  let dialogWidth = "50vw";

  if (editAction !== "edit" && (type === "podcast" || type === "simulation")) {
    dialogWidth = "380px";
  }
  
  useEffect(() => {
    if (show) {
      let bannerValue = banner;
      if(banner){
        bannerValue = {
          ...banner,
          filter: null,
        };
      }
      setBannerState(bannerValue);
    }
  }, [show, banner]);

  useEffect(() => {
    if (uploaded) {
      onFinish({
        ...bannerState!,
        background: uploadedBackground
      });

      const mediaId = _.get(bannerState, 'background.id');
      if (mediaId) {
        dispatch(invalidateMediaUpload(mediaId));
      }
    }
  }, [uploaded, uploadedBackground]);

  const previewTransitions = useTransition(editAction !== 'edit', {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const editTransitions = useTransition(editAction === 'edit', {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });


  const handleActionChange = (action: string) => {
    setOriginalBanner(bannerState);
    setEditAction(action as 'edit' | 'crop' | 'position');
  }

  const handleBannerChange = (background: MediaFile) => {
    let bannerRequest = {
      position: 'top',
      autoHeightEnabled: true,
      sharedByEnabled: true,
      crop: undefined,
      bannerHeight: 0,
      filter: null,
      overlayColor: null,
      fontColor: null,
      descriptionOverlay: null
    } as Banner;

    if (banner) {
      bannerRequest = banner;
    }

    bannerRequest = {
      ...bannerRequest,
      background: background,
      crop: null,
    };

    setBannerState(bannerRequest);
    setEditAction(null);
  }

  const handleSaveBanner = async () => {
    if (!editAction) {
      const media = _.get(bannerState, 'background');
      const entityType = _.get(media, 'entityType');

      if (media && entityType === 'media-preview') {
        const mediaFile = media as MediaFile;
        const blob = await fetch(media!.full!).then(r => r.blob());
        const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

        dispatch(uploadMedia({
          id: media.id,
          media: {
            ...media,
            file: file
          } as MediaFile
        }));
      } else {
        onFinish(bannerState!);
      }
    } else {
      setEditAction(null);
    }
  }

  const onCancel = () => {
    if (editAction) {
      if (originalBanner) {
        setBannerState(originalBanner);
      }
      setEditAction(null);
    } else {
      onClose();
    }
  };

  return (
    <Slider.Dialog className={styles.editBannerDialog} show={show} width={dialogWidth} onClose={onClose}>
      <Slider.Body>
        <div className={styles.body}>
          {previewTransitions((style, item) => (
            item && <animated.div style={style} className={cx(
              styles.stack,
              {
                [styles.stackVarient]:(type === "podcast" || type === "simulation") && editAction !== "edit" && editAction !== "crop",
              }
            )}>
              <PreviewThumbnail
                type={type}
                entityType={entityType}
                banner={bannerState}
                defaultBanner={defaultBanner}
                editAction={editAction}
                onSave={handleSaveBanner}
                onActionChange={handleActionChange}
                onClose={onCancel}
                onCropChange={(crop) => setBannerState(prev => ({ ...prev!, crop }))}
                onPositionChange={(position) => setBannerState(prev => ({ ...prev!, position }))}
              />
            </animated.div>
          ))}

          {editTransitions((style, item) => (
            item && <animated.div style={style} className={cx(
              styles.stack,
              {
                [styles.stackVarient]:(type === "podcast" || type === "simulation") && editAction !== "edit" && editAction !== "crop",
              }
            )}>
              <ChooseMedia onChange={handleBannerChange} onClose={() => { setEditAction(null) }} />
            </animated.div>
          ))}

        </div>
      </Slider.Body>
    </Slider.Dialog >
  );
};

interface PreviewThumbnailProps {
  type: 'course' | 'simulation' | 'podcast';
  entityType?: 'discovery' | 'coldCall' | 'elevatorPitch'
  banner: Banner | null;
  defaultBanner: string;
  editAction: 'edit' | 'crop' | 'position' | null;
  // eslint-disable-next-line no-unused-vars
  onActionChange: (action: string) => void;
  onSave: () => void;
  onClose: () => void;
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void;
  onPositionChange: (position: string) => void;
}

const PreviewThumbnail: React.FC<PreviewThumbnailProps> = ({ type, entityType, banner, defaultBanner, editAction, onActionChange, onSave, onClose, onCropChange, onPositionChange }) => {
  const icon = banner?.background;
  const crop = banner?.crop;
  const position = banner?.position;

  const uploading = useSelector((state: any) => {
    const background = _.get(banner, `background`);
    const entityType = _.get(background, 'entityType');
    if (background && entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loading`, false);
    }

    return false;
  });

  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <h2>customize your banner</h2>
        <p>pick your preferred image to match your styles.</p>
      </div>
      <div className={cx(
        styles.previewBody,
        {
          [styles.previewVarient]: (type === "podcast" || type === "simulation") && editAction !== "crop",
        }
      )}>

        {editAction !== 'crop' && (
          <Thumbnail
            media={icon}
            className={cx(
              styles.thumbnail,
              styles[type],              
              entityType && styles[entityType]
            )}
            crop={crop}
            position={position}
            alt=""
            defaultImage={defaultBanner}
          />)}

        {editAction === 'crop' && (
          <CropBanner banner={banner} onCropChange={onCropChange} />
        )}

        {editAction === 'position' && (
          <BannerPositionMenu handlePositionChange={onPositionChange} />
        )}

        {(editAction !== 'crop' && editAction !== 'position') && <EditToolbar show={true} banner={banner} onActionChange={onActionChange} />}

      </div>
      <div className={styles.actions}>
        <Button variant="primary" icon='paperflite-tick' disabled={uploading} onClick={onSave}>{uploading ? 'Uploading...' : editAction === null ? 'Confirm' : 'Save'}</Button>
        <Button variant="secondary" disabled={uploading} onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

interface ChooseMediaProps {
  // eslint-disable-next-line no-unused-vars
  onChange: (background: MediaFile) => void;
  onClose: () => void;
}

const ChooseMedia: React.FC<ChooseMediaProps> = ({ onChange, onClose }) => {

  const handleChange = (background: MediaFile) => {
    onChange(background);
  }

  const handleBannerSelection = (media: MediaLibrary) => {
    if (!media) {
      return;
    }

    const imageUrl = media.imageUrl;
    if (!imageUrl) {
      return;
    }

    fetch(imageUrl)
      .then((response) => {
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);

        const backgroundMedia = {
          id: nanoid(6),
          entityType: 'media-preview',
          type: media.mediaType,
          full: url,
          thumbnail: url,
          width: media.width,
          height: media.height,
          contentType: blob.type,
        } as MediaFile

        handleChange(backgroundMedia);
      }).catch(error => {
        console.error(error);
      });
  }

  const handleFileChange = async (e: any) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const backgroundMedia = await parseMediaMetadata(file) as MediaFile;
    handleChange(backgroundMedia);
  }

  return (
    <div className={styles.chooseMedia}>
      <div className={styles.mediaHeader}>
        <button className={styles.backButton} onClick={onClose}><i className={cx('paperflite-left-arrow')}></i></button>
        <div className={styles.header}>
          <h2>Choose Media</h2>
          <p>Choose from a variety of media to use as your banner background.</p>
        </div>
        <button className={styles.uploadButton}>
          <i className={cx('paperflite-upload')}></i> Upload
          <input type="file" title='' accept={"image/*,video/*"} onChange={handleFileChange}></input>
        </button>
      </div>
      <div className={styles.mediaLibrary}>
        <MediaLibraryChooser entityType="collection" onChange={handleBannerSelection} columns={4} />
      </div>
    </div>
  );
}

interface CropBannerProps {
  banner: Banner | null;
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void;
}

const CropBanner: React.FC<CropBannerProps> = ({ banner, onCropChange }) => {
  const bannerImage = _.get(banner, 'background.full');
  const [editorRef, editorBounds] = useMeasure();
  const cropperRef = React.useRef<ReactCropperElement>(null);

  const [crop, setCrop] = React.useState<{ x: number, y: number, width: number; height: number } | null>(_.get(banner, 'crop') || null);
  const [initialized, setInitialized] = React.useState(false);

  return (
    <div ref={editorRef} className={styles.cropBanner}>
      <Cropper ref={cropperRef} src={bannerImage} style={{
        maxWidth: '100%',
        maxHeight: '100%',
      }}
        zoomable={false}
        viewMode={1}
        guides={false}
        minContainerHeight={editorBounds.height}
        ready={() => {
          if (crop) {
            cropperRef.current!.cropper.setData(crop!);
          }
        }}
        data={crop ? {
          x: crop?.x || 0,
          y: crop?.y || 0,
          width: crop?.width || 100,
          height: crop?.height || 100,
        } : undefined}
        autoCrop={true}
        autoCropArea={1}
        crop={(e) => {
          // setCrop(e.detail);
          if (cropperRef.current) {
            const croppedData = cropperRef.current.cropper.getData();
            onCropChange(croppedData);
          }
        }}
      />
    </div>
  );
}

interface EditToolbarProps {
  show: boolean;
  banner: Banner | null;
  // eslint-disable-next-line no-unused-vars
  onActionChange: (action: string) => void;
}

const EditToolbar: React.FC<EditToolbarProps> = ({ show, banner, onActionChange }) => {
  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 }
  });

  const mediaType = banner?.background?.type || 'image';

  return transitions(
    (style, item) =>
      item ? (
        <animated.div style={style} className={cx(styles.toolbar)}>
          <Tooltip content="Edit" placement="top">
            <div className={cx(styles.item)} onClick={() => onActionChange("edit")}>
              <i className={cx(styles.icon, 'paperflite-pen')}></i>
            </div>
          </Tooltip>
          {mediaType === 'image' && (
            <Tooltip content="Crop" placement="top">
              <div className={cx(styles.item)} onClick={() => onActionChange("crop")}>
                <i className={cx(styles.icon, 'paperflite-crop')}></i>
              </div>
            </Tooltip>
          )}

          <Tooltip content="Position" placement="top">
            <div className={cx(styles.item)} onClick={() => onActionChange("position")}>
              <i className={cx(styles.icon, 'paperflite-focus')}></i>
            </div>
          </Tooltip>
        </animated.div>
      ) : null
  );
};

interface BannerPositionMenuProps {
  handlePositionChange: (value: string) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ handlePositionChange }) => {

  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
    delay: 500,
  });

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={styles.positionMenu}>
        <Actions.Container>
          <Actions.Row className={styles.positionActionRow}>
            <Actions.Item className={styles.positions}>
              {POSITIONS.map((position) => {
                return (
                  <div
                    key={position.value}
                    className={cx(styles.position)}
                    onClick={() => handlePositionChange(position.value)}
                  />
                );
              })}
            </Actions.Item>
          </Actions.Row>
        </Actions.Container>
      </animated.div>
    ) : null
  );
};

export { EditCourseThumbnail };